import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// UM PEDIDO POR VEZ — "Ative os avisos" e "Instale o app" nunca aparecem
// juntos, nem na primeira abertura do dia, nem nos primeiros 20 segundos,
// nem antes de a pessoa rolar a página. No máximo 1 por sessão.
// "Agora não" esconde por 7 dias.
// ---------------------------------------------------------------------------

export type PedidoApp = 'instalar' | 'avisos';

const CHAVE_PEDIDO_SESSAO = 'seliga:pedido_sessao';
const CHAVE_DIA = 'seliga:pedido_dia';
const CHAVE_AVISOS = 'seliga:avisos_agora_nao';
const CHAVE_INSTALAR = 'seliga:instalar_agora_nao';

const ESPERA_MS = 20000;
const DIAS_SONECA = 7;

function hojeSP(): string {
  try {
    return new Intl.DateTimeFormat('sv-SE', { timeZone: 'America/Sao_Paulo' }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

// Primeira abertura do dia? Então nada de pedido nesta visita.
let primeiraDoDia = false;
try {
  const guardado = localStorage.getItem(CHAVE_DIA);
  if (guardado !== hojeSP()) {
    primeiraDoDia = true;
    localStorage.setItem(CHAVE_DIA, hojeSP());
  }
} catch {
  /* sem armazenamento: segue sem a regra do dia */
}

function pedidoDaSessao(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_PEDIDO_SESSAO);
  } catch {
    return null;
  }
}

function marcarPedidoDaSessao(tipo: PedidoApp): void {
  try {
    sessionStorage.setItem(CHAVE_PEDIDO_SESSAO, tipo);
  } catch {
    /* ignora */
  }
}

function adiado(chave: string): boolean {
  try {
    const ate = Number.parseInt(localStorage.getItem(chave) || '0', 10) || 0;
    return Date.now() < ate;
  } catch {
    return false;
  }
}

function adiar(chave: string): void {
  try {
    localStorage.setItem(chave, String(Date.now() + DIAS_SONECA * 24 * 3600 * 1000));
  } catch {
    /* ignora */
  }
}

export function avisosAdiado(): boolean {
  return adiado(CHAVE_AVISOS);
}

export function adiarAvisos(): void {
  adiar(CHAVE_AVISOS);
}

export function instalarAdiado(): boolean {
  return adiado(CHAVE_INSTALAR);
}

export function adiarInstalar(): void {
  adiar(CHAVE_INSTALAR);
}

// A pessoa já tem compra/inscrição? Aí o convite para instalar ganha a frente
// e muda o texto ("Guarde seu ingresso no app").
let comCompra = false;

export function marcarTemCompra(): void {
  comCompra = true;
}

export function temCompra(): boolean {
  return comCompra;
}

// ---------------------------------------------------------------------------
// Portão compartilhado: um único dono por abertura do app.
// ---------------------------------------------------------------------------

const nascimento = Date.now();
let rolou = false;
let aberto = false;
let dono: PedidoApp | null = null;
let timer: number | null = null;
let escutando = false;
const desejos = new Map<PedidoApp, number>();
const ouvintes = new Set<() => void>();

function avisar(): void {
  ouvintes.forEach((fn) => fn());
}

function liberar(): void {
  if (dono) return;
  if (primeiraDoDia) return;
  if (pedidoDaSessao()) return;
  if (!aberto) return;
  let escolhido: PedidoApp | null = null;
  let menor = Number.POSITIVE_INFINITY;
  desejos.forEach((prioridade, tipo) => {
    const efetiva = tipo === 'instalar' && comCompra ? prioridade - 5 : prioridade;
    if (efetiva < menor) {
      menor = efetiva;
      escolhido = tipo;
    }
  });
  if (!escolhido) return;
  dono = escolhido;
  marcarPedidoDaSessao(escolhido);
  avisar();
}

function abrirQuandoPuder(): void {
  if (aberto) return;
  if (Date.now() - nascimento < ESPERA_MS) return;
  if (!rolou) return;
  aberto = true;
  liberar();
}

function iniciarEspera(): void {
  if (escutando) return;
  escutando = true;

  const aoRolar = () => {
    if (window.scrollY > 120) rolou = true;
    if (rolou) abrirQuandoPuder();
  };
  window.addEventListener('scroll', aoRolar, { passive: true });
  aoRolar();

  const tique = () => {
    abrirQuandoPuder();
    if (!aberto) timer = window.setTimeout(tique, 1000);
    else timer = null;
  };
  timer = window.setTimeout(tique, 1000);
}

/**
 * Pede a vez de mostrar um pedido. `quer` diz se este ponto quer aparecer
 * agora; o retorno diz se é a vez dele.
 */
export function usePedidoUnico(tipo: PedidoApp, quer: boolean, prioridade = 2): boolean {
  const [, forcar] = useState(0);

  useEffect(() => {
    const ouvinte = () => forcar((n) => n + 1);
    ouvintes.add(ouvinte);
    return () => {
      ouvintes.delete(ouvinte);
    };
  }, []);

  useEffect(() => {
    if (quer) {
      desejos.set(tipo, prioridade);
      iniciarEspera();
      liberar();
    } else {
      desejos.delete(tipo);
      if (dono === tipo) {
        dono = null;
        avisar();
      }
    }

    return () => {
      desejos.delete(tipo);
      if (dono === tipo) {
        dono = null;
        avisar();
      }
    };
  }, [tipo, quer, prioridade]);

  return dono === tipo;
}